import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GenericModalService, ModalSettingsConfirm } from '../../shared/generic-modal/generic-modal.service';
import { JsonHttpService } from '../../shared/http/json-http.service';
import { LinkHost } from '../models/link-host';
import { AppInsightsInfo } from '../models/app-insights-info';
import { firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class SystemService {

    private static currentVersion: string;

    constructor(private httpService: JsonHttpService, private genericModalService: GenericModalService) { }

    /**
     * Gets the current version. This method doesn not require the user to be logged in.
     */
    public async getVersionInfo(): Promise<LinkHost[]> {

        // Bypass credentials by using empty headers....
        // let headers: Headers = new Headers();
        // let args: RequestOptionsArgs = { headers: headers };
        const result = await this.httpService.get('/api/system/get-version-info', undefined, true, true).toPromise();
        return result;
    }

    private versionCheckerAlreadyShown = false;
    public async startVersionCheckTimer() {
        const checker = async () => {
            if (this.versionCheckerAlreadyShown) {
                return;
            }

            const version = await this.checkCurrentUIVersion();

            // Skip the version check dialog if the version is 1.0.0 (the version used with debug builds).
            if (version === '1.0.0') {
                return;
            }

            if (SystemService.currentVersion && version && SystemService.currentVersion !== version) {
                this.versionCheckerAlreadyShown = true;
                const settings = new ModalSettingsConfirm();
                settings.size = 'lg';
                settings.backdrop = 'static';
                settings.title = 'New version';
                settings.body = `The Link UI has been updated from version ${SystemService.currentVersion} to ${version}. Please refresh your browser by hitting F5 or the refresh button.`;
                settings.okButtonText = 'Refresh now';
                settings.cancelButtonText = 'Close';

                const refresh = () => {
                    window.location.reload();
                };

                this.genericModalService.message(settings).then(result => refresh()).catch(() => { }).finally(() => this.versionCheckerAlreadyShown = false);
            }

            if (version) {
                SystemService.currentVersion = version;
            }
        };

        checker();
        setInterval(await checker, 60000); // Check every minute...
    }

    public async checkCurrentUIVersion(): Promise<string> {
        const options = { params: new HttpParams().set('host', 'UI') };

        try {
            // Bypass generic error-handling to supress the "No internet connection" and "Temporarily unavailable" error-dialogs during an update.
            const result: string = await this.httpService.get('/api/system/get-latest-version-info-for-host', options, false, true).toPromise();
            return result;
        }
        catch (error) {
            console.error(error);
            return undefined;
        }
    }

    public async sendTestMail(from: string, to: string): Promise<any> {
        return await this.httpService.post('/api/system/send-test-mail', { from, to }).toPromise();
    }

    public async getAppInsightsInfo(): Promise<AppInsightsInfo> {
        return await firstValueFrom(this.httpService.getTyped<AppInsightsInfo>('/api/system/get-application-insights-info'));
    }
}
